import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link2, ReceiptText } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (value, currency = "EUR") => new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(Number(value || 0));

export function ProjectCostLinksPanel({ projectId }) {
  const [available, setAvailable] = useState([]);
  const [linked, setLinked] = useState([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    if (!projectId) return;
    try {
      const [choices, overview] = await Promise.all([
        axios.get(`${API}/crossstudio-links/projects/${projectId}/linkable-incoming-invoices`),
        axios.get(`${API}/crossstudio-links/projects/${projectId}/overview`),
      ]);
      setAvailable(choices.data || []);
      setLinked(overview.data?.incoming_invoices || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Projektkosten konnten nicht geladen werden"); }
  };
  useEffect(() => { load(); }, [projectId]);
  const link = async () => {
    if (!selected) return toast.error("Bitte eine Eingangsrechnung auswählen");
    setBusy(true);
    try { await axios.post(`${API}/crossstudio-links/projects/${projectId}/incoming-invoices/${selected}/link`); setSelected(""); toast.success("Eingangsrechnung dem Projekt zugeordnet"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Kostenverknüpfung fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  const unlink = async (id) => {
    if (!window.confirm("Projektkosten-Verknüpfung lösen? Die Eingangsrechnung bleibt im Expense Studio erhalten.")) return;
    try { await axios.post(`${API}/crossstudio-links/projects/${projectId}/incoming-invoices/${id}/unlink`); toast.success("Projektzuordnung gelöst"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Verknüpfung konnte nicht gelöst werden"); }
  };
  const choices = available.filter((item) => !item.projekt_id);
  return <Card className="mt-3 p-4 border-[var(--brand-border-light)]" data-testid="project-cost-links-panel">
    <div className="text-sm font-semibold text-[var(--brand-ink)] flex items-center gap-1"><ReceiptText className="w-4 h-4 text-[var(--brand-primary)]" /> Projektkosten aus Expense Studio</div>
    <p className="text-[11px] text-[var(--brand-muted)] mt-1">Eingangsrechnungen bleiben im Expense Studio und werden nur diesem Projekt zugeordnet. Sie fließen danach transparent in die Profitabilität ein.</p>
    {linked.length ? <div className="mt-2 space-y-1">{linked.map((item) => <div className="flex justify-between gap-3 text-xs" key={item.id}><span className="truncate">{item.lieferant_name || item.rechnungsnummer || "Eingangsrechnung"}</span><span className="shrink-0">{money(item.betrag_netto || item.betrag_brutto, item.waehrung || "EUR")}</span><button className="text-red-600 hover:underline shrink-0" onClick={() => unlink(item.id)}>Lösen</button></div>)}</div> : <p className="mt-2 text-xs text-[var(--brand-muted)]">Noch keine Eingangsrechnung zugeordnet.</p>}
    <div className="mt-3 flex flex-col sm:flex-row gap-2"><select className="h-9 flex-1 rounded border border-[var(--brand-border)] px-2 text-sm" value={selected} onChange={(e) => setSelected(e.target.value)}><option value="">– vorhandene Eingangsrechnung zuordnen –</option>{choices.map((item) => <option value={item.id} key={item.id}>{item.lieferant_name || "Lieferant"} · {money(item.betrag_netto || item.betrag_brutto, item.waehrung || "EUR")}</option>)}</select><Button size="sm" disabled={!selected || busy} onClick={link}><Link2 className="w-3.5 h-3.5 mr-1" /> Kosten zuordnen</Button></div>
  </Card>;
}
